import {
  Button,
  CheckboxGroup,
  LayoutGridContainer,
  LayoutVerticalSpacer,
  TappableLinkContainer,
  TextField,
} from '@madpaws/design-system';
import { useFormikContext } from 'formik';
import React from 'react';

import { LEGAL_LINKS } from '~/common/constants/app';

import styles from './EmailSignUpForm.module.css';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import { CHECKBOX_OPTIONS, CHECKBOXGROUP_NAME } from '../constants';

import type { EmailSignUpFormValues } from './EmailSignUpDialog';
import type { CheckboxGroupOnChange } from '@madpaws/design-system';
import type { ChangeEvent, ReactElement } from 'react';

type Props = {
  apiError?: string | null;
};

export const EmailSignUpForm = ({ apiError }: Props): React.ReactElement => {
  const {
    isSubmitting,
    submitForm,
    setFieldTouched,
    setFieldValue,
    values,
    isValid,
  } = useFormikContext<EmailSignUpFormValues>();

  const isSubmitButtonDisabled = isSubmitting || !isValid;

  const onTextFieldChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const {
      target: { name, value },
    } = event;
    setFieldTouched(name, true, false);
    setFieldValue(name, value);
  };

  const onCheckboxChange = ({ target }: CheckboxGroupOnChange): void => {
    setFieldValue(CHECKBOXGROUP_NAME, target.value.value);
  };

  const renderLegalLinks = (link: typeof LEGAL_LINKS[0], index: number): ReactElement => (
    <TappableLinkContainer key={index}>
      <a href={link.href} rel="noopener noreferrer" target="_blank">
        {link.label}
        {index === 0 && <span>|</span>}
      </a>
    </TappableLinkContainer>
  );

  return (
    <LayoutGridContainer rowGap="small2x">
      <LayoutVerticalSpacer space="small4x">
        <ErrorBoundary fieldName="email">
          <TextField label="Email" name="email" onChange={onTextFieldChange} value={values.email} />
        </ErrorBoundary>

        <div className={styles.nameFields}>
          <ErrorBoundary fieldName="firstName">
            <TextField
              label="First name"
              name="firstName"
              onChange={onTextFieldChange}
              value={values.firstName}
            />
          </ErrorBoundary>

          <ErrorBoundary fieldName="lastName">
            <TextField
              label="Last name"
              name="lastName"
              onChange={onTextFieldChange}
              value={values.lastName}
            />
          </ErrorBoundary>
        </div>

        <ErrorBoundary fieldName="password">
          <TextField
            label="Password"
            name="password"
            onChange={onTextFieldChange}
            type="password"
            value={values.password}
          />

          <p className={styles.passwordHint}>
            Use at least 8 characters with a capital letter, number, and symbol.
          </p>
        </ErrorBoundary>

        <div className={styles.checkboxGroup}>
          <ErrorBoundary fieldName={CHECKBOXGROUP_NAME}>
            <CheckboxGroup
              legend=""
              name={CHECKBOXGROUP_NAME}
              onChange={onCheckboxChange}
              options={CHECKBOX_OPTIONS}
              value={{ value: values.checkboxOptions }}
            />
          </ErrorBoundary>
        </div>
      </LayoutVerticalSpacer>
      <div className={styles.privacyLinks}>{LEGAL_LINKS.map(renderLegalLinks)}</div>

      <>{apiError && <p className={styles.error}>{apiError}</p>}</>
      <Button
        isDisabled={isSubmitButtonDisabled}
        isFullBleed
        label="Sign up"
        onClick={submitForm}
      />
    </LayoutGridContainer>
  );
};
